<template>
  <go-go-google-map
    :is-draggable="!disabled"
    :is-open-default="true"
    :map-center="mapCenter"
    :markers="[
      ...markers,
      ...(mainMarker ? [mainMarker] : []),
      ...(additionalPins ? additionalPins : [])
    ]"
    :zoom="18"
    class="mb-0 mt-10 mobile-mt-10"
    is-side-by-side
    map-type-id="satellite"
    @change="setMarkerPosition"
    @drag-pin="$emit('drag-pin')"
    @click-marker="onClickMarker"
  />
  <div class="map-pin-notes">
    <div v-if="isPickup">
      <span>Pickup:</span>
      <img src="/static/img/pickup_marker.png">
    </div>
    <div v-else>
      <span>Dropoff:</span>
      <img src="/static/img/dropoff_marker.png">
    </div>
    <div>
      <span>Original:</span>
      <img src="/static/img/gray_marker.png">
    </div>
    <div v-if="hasVenueMarkers">
      <span>Uber Suggested:</span>
      <img src="/static/img/multi_stops_marker.png">
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref, Ref } from 'vue';

import { Venue, VenueSelection } from '@/models/venue';
import type { CustomLocationType, GeoLocationType, MapMarker } from '@/models/address';
import util from '@/Utils/util';
import {
  ORIGINAL_ADDRESS_ITEM,
  buildEntranceOptions,
  getZoneLocationByEntrance,
} from './venueInfo';

const props = defineProps<{
  venues?: Venue[] | null;
  address: CustomLocationType;
  originalCoordinates: GeoLocationType | null;
  additionalPins?: MapMarker[];
  disabled?: boolean;
  isPickup?: boolean;
}>();

const emit = defineEmits<{
  (event: 'selected', selection: VenueSelection): void;
  (event: 'drag-pin'): void;
}>();

const mapCenter: Ref<GeoLocationType> = ref({
  lat: 37.42248,
  lng: -122.08547,
});
const mainMarker: Ref<MapMarker | null> = ref(null);

const entranceOptions = computed(() => buildEntranceOptions(props.venues));

const createMarker = (
  lat: number,
  lng: number,
  index: number,
  label?: string,
  draggable = false,
  icon = '/static/img/multi_stops_marker.png',
  zIndex?: number,
  clickable?: boolean,
) => ({
  position: { lat, lng },
  initialPosition: { lat, lng },
  index,
  isDraggable: draggable,
  isShow: true,
  icon,
  label,
  zIndex,
  isClickable: clickable ?? true,
});

const markers = computed(() => {
  const array: MapMarker[] = [];

  const { lat, lng } = props.originalCoordinates || {};

  if (!lat || !lng) {
    return array;
  }

  array.push(createMarker(lat, lng, 0, undefined, false, '/static/img/gray_marker.png', 0));

  let mapIndex = 1;
  for (const option of entranceOptions.value ?? []) {
    const info = getZoneLocationByEntrance(option.key, props.venues, entranceOptions.value);

    if (!info || !info.location) {
      continue;
    }

    array.push(
      createMarker(info.location.latitude, info.location.longitude, mapIndex, `${mapIndex}`, false, '/static/img/multi_stops_marker.png', mapIndex)
    );
    mapIndex++;
  }

  return array;
});

const hasVenueMarkers = computed(() => markers.value.length > 1);

const updateMapCenter = (lat: number, lng: number) => {
  mapCenter.value = { lat, lng };
};

const updateMainMarker = (lat: number, lng: number) => {
  if (!mainMarker.value) {
    mainMarker.value = createMarker(
      lat,
      lng,
      markers.value.length,
      undefined,
      true,
      props.isPickup ? '/static/img/pickup_marker.png' : '/static/img/dropoff_marker.png',
      9999,
      false,
    );
  } else {
    mainMarker.value = {
      ...mainMarker.value,
      position: { lat, lng },
      initialPosition: { lat, lng },
    };
  }

  updateMapCenter(lat, lng);
};

const onClickMarker = (index: number) => {
  const marker = markers.value[index];
  if (!marker) {
    return;
  }

  updateMainMarker(marker.position.lat, marker.position.lng);

  const entranceOptionKey = entranceOptions.value?.[index]?.key ?? ORIGINAL_ADDRESS_ITEM.key;
  const info = getZoneLocationByEntrance(entranceOptionKey, props.venues, entranceOptions.value);

  emit('selected', {
    venue: info?.venue,
    zone: info?.zone,
    location: info?.location,
    name: info?.name ?? '',
    position: {
      lat: Number(marker.position.lat),
      lng: Number(marker.position.lng),
    },
  });
};

const setMarkerPosition = (
  lat: number | string | null,
  lng: number | string | null
) => {
  if (!lat || !lng) {
    return;
  }

  util.updateMarkerPosition(mainMarker.value, lat, lng, false);

  // Free pin drag means Original Address / custom coords — clear Uber place selection
  // so we do not keep sending start_place for a nearby venue the operator left.
  emit('selected', {
    name: '',
    position: {
      lat: Number(lat),
      lng: Number(lng),
    },
  });
};

watch(() => props.address, () => {
  if (!props.address.latitude || !props.address.longitude) {
    mainMarker.value = null;
    return;
  }

  updateMainMarker(Number(props.address.latitude), Number(props.address.longitude));
}, {
  deep: true,
  immediate: true,
});
</script>

<style lang="scss" scoped>
.map-pin-notes {
  display: flex;
  justify-content: space-between;
  background: #c1c1c1;
  padding: 5px;
  margin-bottom: 10px;

  img {
    width: 30px;
  }
}
</style>
